import React, { useCallback, useEffect, useState } from 'react';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { LinearProgress } from '@material-ui/core';
import Bytes from '@cube3/cubicle/src/core/utility/Bytes';
import { HBox, VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';
import { Panel } from '@cube3/cubicle/src/core/atoms/Panel';
import { getEnvVar } from '../../../../utils/getEnvVar';
// import { StatusBlip } from '@cube3/ui/src/StatusBlip';
import { Icon } from '@cube3/cubicle/src/core/atoms/Icon';
import { useWorkspacePermissions } from '@cube3/state/src/redux/components/Hooks/usePermission';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography/Typography';
import { Button } from '@cube3/cubicle/src/core/atoms/Button/Button';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { isMobile } from 'react-device-detect';
import { MenuItem } from '@cube3/cubicle/src/core/atoms/MenuItem';
import { useMenuToggle } from '@cube3/cubicle/src/helpers/hooks/useMenuToggle';
import useOutsideClick from '@cube3/cubicle/src/helpers/hooks/UseOutsideClick';
import { MobileSlideSurface } from '@cube3/cubicle/src/components/Surface/MobileSlideSurface';
import { useTrialWorkspaceCheck } from '../AdminCenter/hooks/useTrialWorkspaceCheck';

const allItemsParams = {
  page: { size: -1 } // get all items
};

// when to show warning indicators / colors
const THRESHOLD = 0.9; // 0 - 1 range

export const Storage = () => {
  const [workspaceId, workspace] = useCurrentWorkspace();
  const [isAdmin] = useWorkspacePermissions(['WORKSPACE_WRITE']);

  const { isTrial, onClickUpgrade } = useTrialWorkspaceCheck()

  const { resources: accounts, status: accountStatus } = useResourceList__ALPHA(
    {
      resourceType: 'workspace',
      resourceId: workspaceId,
      edgeType: 'account',
      edgeLabel: 'accounts',
      params: allItemsParams
    }
  );

  const { resources: roles, status: rolesStatus } = useResourceList__ALPHA({
    resourceType: 'workspace',
    resourceId: workspaceId,
    edgeType: 'role',
    edgeLabel: 'roles',
    params: allItemsParams
  });

  // storage counter
  const storageFactor = workspace
    ? workspace.maximum_allowed_storage !== 0
      ? workspace.used_storage / workspace.maximum_allowed_storage
      : 0
    : undefined;

  // seats counter
  const billableAccounts = accounts?.filter(
    (a) => roles.find((r) => r.id === a.relationships.role.id)?.billable
  ).length;

  const seatsFactor =
    workspace && hasResolved(accountStatus) && hasResolved(rolesStatus)
      ? workspace.maximum_allowed_accounts !== 0
        ? billableAccounts / workspace.maximum_allowed_accounts
        : 0
      : undefined;

  // showmore
  const [showmore, setShowMore] = useState(false);
  const handleClick = useCallback(
    (ev) => {
      ev.stopPropagation();
      setShowMore((d) => !d);
    },
    [setShowMore]
  );

  // close when clicked outside
  useEffect(() => {
    if (showmore) {
      const handleClose = () => {
        setShowMore(false);
      };
      window.addEventListener('click', handleClose);
      return () => {
        window.removeEventListener('click', handleClose);
      };
    }
  }, [showmore]);

  // sales button
  const contactUsUrl =
    getEnvVar('CONTACT_US_URL') || `https://www.cube-cloud.com/contact`;

  const handleContactClick = useCallback(
    (ev) => {
      ev.stopPropagation();
      window.open(contactUsUrl, '_blank', 'noopener noreferrer');
    },
    [contactUsUrl]
  );

  if (!workspace) {
    return null;
  }

  return (
    <Panel
      padding={[6, 3, 6, 3]}
      backgroundColor={
        isMobile
          ? 'transparent'
          : 'linear-gradient(180deg, #141414 0%, #141414 100%)'
      }
      hoverBackgroundColor={
        isMobile ? 'none' : (theme) => theme.color.background.neutral
      }
      style={{ cursor: 'pointer' }}
      onClick={handleClick}
    >
      <VBox style={{ gap: 8 }}>
        <HBox
          style={{
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {/* {(storageFactor > THRESHOLD || seatsFactor > THRESHOLD) && (
            <StatusBlip size="tiny" color="warning" glimmer={true} />
          )} */}
          <Typography lineHeight="trim" colorVariant="02" variant="md">
            {workspace?.plan_name
              ? `${workspace.plan_name} plan`
              : 'Workspace plan'}{' '}
          </Typography>
          {!isMobile && (
            <>
              {!showmore ? (
                <Icon
                  icon="unfold_more"
                  colorVariant="02"
                  size={SizeVariants.md}
                />
              ) : (
                <Icon
                  icon="unfold_less"
                  colorVariant="02"
                  size={SizeVariants.md}
                />
              )}
            </>
          )}
        </HBox>
        <div
          style={{
            filter: storageFactor < THRESHOLD ? 'grayscale(1)' : undefined
          }}
        >
          <LinearProgress
            value={storageFactor * 100}
            variant={
              storageFactor !== undefined ? 'determinate' : 'indeterminate'
            }
          />
        </div>

        {workspace && (
          <Typography lineHeight="trim" colorVariant="03" variant="sm">
            <span>
              {Math.ceil(storageFactor * 100)}% used (
              <Bytes
                size={workspace.used_storage.toString()}
                inFormat="B"
                fullForm={false}
                placement={'bottom'}
                toolTip={false}
                decimalNumbers={1}
              />{' '}
              of{' '}
              {workspace.maximum_allowed_storage > 0 ? (
                <Bytes
                  size={workspace.maximum_allowed_storage.toString()}
                  inFormat="B"
                  fullForm={false}
                  placement={'bottom'}
                  toolTip={false}
                  decimalNumbers={1}
                />
              ) : (
                'Unlimited'
              )}
              )
            </span>
          </Typography>
        )}
        {(!isMobile ? showmore : true) && (
          <>
            <div
              style={{
                filter: seatsFactor < THRESHOLD ? 'grayscale(1)' : undefined
              }}
            >
              <LinearProgress
                value={seatsFactor * 100}
                variant={
                  seatsFactor !== undefined ? 'determinate' : 'indeterminate'
                }
              />{' '}
            </div>
            <Typography lineHeight="trim" colorVariant="03" variant="sm">
              {billableAccounts} of{' '}
              {workspace?.maximum_allowed_accounts > 0
                ? workspace?.maximum_allowed_accounts
                : 'Unlimited'}{' '}
              seats used{' '}
            </Typography>
            {isAdmin && (
              <Button
                label={isTrial ? 'Upgrade' : "Contact sales"}
                fullWidth={true}
                buttonStyle="solid"
                background="secondary"
                size={SizeVariants.sm}
                onClick={isTrial ? onClickUpgrade : handleContactClick}
              />
            )}
          </>
        )}
      </VBox>
    </Panel>
  );
};

export const StorageIndicator = () => {
  const { toggle, close, setAnchor, isMenuOpen } = useMenuToggle();

  const rootRef = useOutsideClick(close);

  return (
    <>
      {isMobile ? (
        <div ref={rootRef}>
          <MenuItem
            value="Storage Indicator"
            itemIcon="hard_drive"
            onClick={toggle}
            extraButtonProperties={{ buttonRef: setAnchor }}
            selected={isMenuOpen}
          />
          {isMenuOpen && (
            <MobileSlideSurface onClose={close}>
              <Storage />
            </MobileSlideSurface>
          )}
        </div>
      ) : (
        <Storage />
      )}
    </>
  );
};
